/**
 * 전국 인덱스 표면 정의.
 *
 * 타입별로 인덱스를 나눈 이유: 네이버에서 실제로 검색되는 말이 "장지" 가 아니라
 * "수목장", "납골당" 이다. 한 인덱스에 묶어두면 두 헤드 키워드를 한 페이지가
 * 나눠 갖는다.
 *
 * 타입별 시군구 페이지를 새로 만들지는 않는다. 시군구 페이지는
 * /region/facility/{시도}/{시군구}/ 하나를 공유하고, 타입 인덱스는 그 페이지로
 * 링크 텍스트만 다르게("수원시 납골당", "수원시 수목장") 흘린다.
 * 타입별로 시군구 페이지를 복제하면 내용이 거의 같은 페이지가 358개 생긴다.
 *
 * 묘지는 594곳으로 세 유형 중 가장 크다. /region/facility/ 전체 인덱스만으로
 * 두면 Nav 에서 닿지 않으므로 같은 패턴의 표면을 하나 더 둔다.
 *
 * 화장장은 장지가 아니라 별도 표면이다. 데이터 원본(hwajang_detail.json)과
 * 요금 체계(관내, 인근, 관외 3단)가 장지와 다르고 검색어도 갈린다.
 */
export type RegionIndexKey =
  | "parlor"
  | "facility"
  | "crematorium"
  | "charnel"
  | "natural"
  | "cemetery";

/** facilities_detail.json 의 type 값 */
export type BurialTypeFilter = "Charnel" | "NaturalBurial" | "Cemetery";

export interface RegionIndexSurface {
  /**
   * URL 의 kind 조각. 타입 인덱스도 시군구는 facility 페이지를 공유한다.
   * 화장장만 자기 시군구 페이지를 따로 갖는다(아래 주석 참고).
   */
  routeKind: "parlor" | "facility" | "crematorium";
  /** 이 인덱스 자신의 경로 */
  href: string;
  /** 목록과 제목에 붙는 말 */
  label: string;
  /** 같은 타입을 부를 다른 이름. description, keywords 에 함께 넣는다 */
  aliases: string[];
  /** 지정되면 그 타입의 장지만 센다 */
  burialType?: BurialTypeFilter;
  eyebrow: string;
}

export const REGION_INDEX: Record<RegionIndexKey, RegionIndexSurface> = {
  parlor: {
    routeKind: "parlor",
    href: "/region/parlor/",
    label: "장례식장",
    aliases: [],
    eyebrow: "빈소 비용, 주차, 시설 정보 한눈에 알아보기",
  },
  /**
   * 화장장은 장지 세 유형과 달리 자기 시군구 페이지를 갖는다.
   *
   * 전국 63곳이라 시군구당 0~1곳이고, 관내 시설 유무로 거르면 페이지가 거의
   * 남지 않는다. 그런데 "수원 화장장" 은 실제로 검색되는 말이다. 수원에 화장장이
   * 1곳뿐이어도 인근 화장장을 거리순으로 붙이면 그 페이지가 성립한다.
   * memorial-map.kr 이 같은 구조로 시군구 페이지를 세워 두었다.
   *
   * 장지처럼 facility 시군구 페이지를 공유하지 않는 이유는 H1 이다.
   * "수원시 장지" 페이지에 화장장 검색어를 태우면 제목이 의도와 어긋난다.
   */
  crematorium: {
    routeKind: "crematorium",
    href: "/region/crematorium/",
    label: "화장장",
    aliases: ["화장터", "승화원", "화장시설"],
    eyebrow: "화장장 관내, 관외 요금과 위치 한눈에 알아보기",
  },
  facility: {
    routeKind: "facility",
    href: "/region/facility/",
    label: "장지",
    aliases: ["봉안당", "자연장", "묘지"],
    eyebrow: "봉안당, 자연장, 묘지 비용과 위치 한눈에 알아보기",
  },
  charnel: {
    routeKind: "facility",
    href: "/region/facility/charnel/",
    label: "봉안당",
    aliases: ["납골당", "봉안시설"],
    burialType: "Charnel",
    eyebrow: "납골당 안치단 비용과 위치 한눈에 알아보기",
  },
  natural: {
    routeKind: "facility",
    href: "/region/facility/natural/",
    label: "자연장",
    aliases: ["수목장", "화초장", "잔디장"],
    burialType: "NaturalBurial",
    eyebrow: "수목장, 화초장, 잔디장 비용과 위치 한눈에 알아보기",
  },
  cemetery: {
    routeKind: "facility",
    href: "/region/facility/cemetery/",
    label: "묘지",
    aliases: ["묘원", "공원묘지", "봉안묘"],
    burialType: "Cemetery",
    eyebrow: "공원묘지, 묘원 분양가와 위치 한눈에 알아보기",
  },
};
